// The LIB settings console route subtree (console slice 4 — OPAC & Discovery).
// Code-based routes per DEV-GUIDE-002 §4 — a package-composed subtree hand-writes
// `.lazy()` + `createLazyRoute`. This factory hangs the `/library/settings` tree
// off the portal's root route so router.tsx stays the single wiring point
// (composed ALONGSIDE the catalogue + patrons subtrees — none touches another).
//
// GATING is per-screen (router.tsx's documented choice): the settings LAYOUT
// route re-checks lib:admin via useSession/<Show> before it renders any child — a
// defence-in-depth mirror of the API's rbac('lib:admin') guard on PUT
// /opac/settings (the UI can only HIDE; the gateway is the authority, ADR-001
// §14.2). Anonymous → /login; signed-in-without-the-grant → an explicit denied
// panel, never a blank Outlet. lib:admin is the settings/config tier (LIB_GRANTS:
// only librarian/owner/admin hold lib:* → lib:admin; assistant/cataloguer do not).
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { panelSearch } from "@erp/ui/side-panel";
import type { PanelSearch } from "@erp/ui/side-panel";
import { libraryQueries } from "../../index.js";
import type { AnyRoute } from "@tanstack/solid-router";

/**
 * The settings shell: gates on lib:admin, then renders the active child.
 * Gating sequence lives in @erp/ui/console-layout; the tab strip is retired in
 * favour of the shell sidebar (module manifest) + header breadcrumbs.
 */
function SettingsLayout() {
  return (
    <ConsoleLayout
      perm="lib:admin"
      deniedMessage="Only a library administrator can open settings."
      skeletonId="settings-skeleton"
      deniedId="settings-denied"
      unreachableId="settings-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/* ───────────────────────── panel URL-as-state (§4.1) ─────────────────────────
 * Each settings screen that edits a record opens ONE named panel, so the closed
 * enum per route is a single name. `?panelId` names the row being edited;
 * absent ⇒ create. A garbage `?panel=` heals to "no panel". */
export const POLICY_PANELS = ["rule"] as const;
export const CALENDAR_PANELS = ["exception"] as const;

export function validatePoliciesSearch(input: Record<string, unknown>): PanelSearch {
  return panelSearch(POLICY_PANELS, input);
}

export function validateCalendarSearch(input: Record<string, unknown>): PanelSearch {
  return panelSearch(CALENDAR_PANELS, input);
}

/**
 * Build the `/library/settings` subtree under a portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). The OPAC &
 * Discovery form is the subtree INDEX (its id is the parent fullPath) so it lands
 * at `/library/settings`; the screen closes its own `.lazy()` loop with
 * `createLazyRoute('/library/settings/')` (the index route keeps the trailing
 * slash in its id — the AllPatrons roster precedent).
 */
export function makeLibSettingsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const settingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/settings",
    component: SettingsLayout,
  });

  // Index = the OPAC & Discovery settings form (opacSettings + updateOpacSettings).
  const opacRoute = createRoute({
    getParentRoute: () => settingsRoute,
    path: "/",
    loader: ({ context }) => {
      void context.queryClient.prefetchQuery(libraryQueries.opacSettings());
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/settings/OpacSettings.js")));

  const institutionRoute = createRoute({
    getParentRoute: () => settingsRoute,
    path: "institution",
  }).lazy(lazyRoute(() => import("../../screens/admin/settings/InstitutionProfile.js")));

  const policiesRoute = createRoute({
    getParentRoute: () => settingsRoute,
    path: "policies",
    validateSearch: validatePoliciesSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/settings/CirculationPolicies.js")));

  const calendarRoute = createRoute({
    getParentRoute: () => settingsRoute,
    path: "calendar",
    validateSearch: validateCalendarSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/settings/AcademicCalendar.js")));

  const notificationsRoute = createRoute({
    getParentRoute: () => settingsRoute,
    path: "notifications",
  }).lazy(lazyRoute(() => import("../../screens/admin/settings/Notifications.js")));

  return [
    settingsRoute.addChildren([opacRoute, institutionRoute, policiesRoute, calendarRoute, notificationsRoute]),
  ];
}
